export interface PlayerRankingsData {
  ranking: number;
  playersFirstName: string;
  playersLastName: string;
  rankingPoints: number;
  playerAge: number | string;
  playerCountry: string;
  playerUuid: string;
  tournamentsPlayed: number;
  matchesWon: number;
  matchesLost: number;
  gamesWon: number;
  gamesLost: number;
  pointsWon: number;
  pointsLost: number;
  abbreviation: string;
  slug: string;
  divisionTypes: string;
  showCityToPublic: boolean;
  showStateToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  showMiddleNameToPublic: boolean;
  playerImage: string | null;
  countryRank?: number;
  stateRank?: number;
  stateAbbreviation?: string;
}

export interface FormattedPlayerRankingsData {
  ranking: number;
  playersFirstName: string;
  playersLastName: string;
  rankingPoints: number;
  playerAge: number | string;
  playerCountry: string;
  playerUuid: string;
  tournamentsPlayed: number;
  matchesWon: number;
  matchesLost: number;
  gamesWon: number;
  gamesLost: number;
  pointsWon: number;
  pointsLost: number;
  abbreviation: string;
  stateAbbreviation: string;
  slug: string;
  divisionTypes: string;
  playerImage: string | null;
  countryRank: string;
  stateRank: string;
}

export interface PlayerRankingsStats {
  rankedPlayersCount: string;
  topPoints: number;
  averagePoints: number;
  playersWith5OrMoreTournaments: string;
  playersWith5OrMoreTournamentsPercentage: number;
}

export interface PlayerRankingsFormattedStats {
  rankedPlayersCount: string;
  topPoints: string;
  averagePoints: string;
  playersWith5OrMoreTournamentsPercentage: string;
}

export interface PlayerRankingsDataRes {
  response: PlayerRankingsData[];
  stats: PlayerRankingsStats;
}

export interface PlayersFiltersInterface {
  rankings: string;
  search: string;
  country: string;
  state: string;
  rankBy: string;
  age: string;
  statsType: string;
  genderType: string;
}

export interface TournamentFiltersInterface {
  search: string;
  country: string;
  state: string;
  year: string;
}

export type TournamentFiltersMenu = 'search' | 'country' | 'state' | 'year';
export type TournamentDropdownType = 'tournamentSearch' | 'country' | 'state' | 'year';
export type APRType = 'player' | 'tournament';

export interface IDTitlePair {
  id: string;
  title: string;
}

export interface ValueLabelPair {
  value: string;
  label: string;
}

export interface CountryFilter extends IDTitlePair {
  stateTitle: string;
}

interface PlayerStats {
  rankingPoints: string;
  tournamentsPlayed: string;
  eventsPlayed: string;
  matchesWon: string;
  matchesLost: string;
  gamesWon: string;
  gamesLost: string;
  pointsWon: string;
  pointsLost: string;
  pointsAllowed: string;
  matchesPercentage: number;
  gamesPercentage: number;
  worldRank: string;
  countryRank: string;
  stateRank: string;
  avgOpponentsRating: number;
  avgPartnersRatings: number;
  worldNumOfPlayers: string;
  countryNumOfPlayers: string;
  stateNumOfPlayers: string;
}

export interface YearlyRecord {
  year: string;
  totalPoints: string;
  totalMatches: string;
  matchesWon: string;
  matchesLost: string;
  totalGames: string;
  gamesWon: string;
  gamesLost: string;
  pointsWon: string;
  pointsLost: string;
  pointsDifference: string;
  rank?: string;
  rankCountry?: string;
  rankState?: string;
}

export interface PlayerProfileData {
  firstName: string;
  lastName: string;
  playerImage: string;
  age: string;
  plays: string;
  gender: string;
  city: string;
  country: string;
  abbreviation: string;
  slug: string;
  heightInches: string;
  heightFeet: string;
  playerLevelId: string;
  race: PlayerStats;
  weeks52: PlayerStats;
  raceHistoryTable: YearlyRecord[];
  ratingsSingles: number;
  ratingsDoubles: number;
  state: string;
  stateAbbreviation: string;
  playerUuid: string;
  countryFlag: string;
  showCityToPublic: boolean;
  showStateToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  countryAbbreviation2Digit?: string;
  stateTitle?: string;
}

export interface APRRankingProfileData {
  firstName: string;
  lastName: string;
  playerImage: string;
  age: string;
  plays: string;
  gender: string;
  city: string;
  country: string;
  abbreviation: string;
  slug: string;
  heightInches: string;
  heightFeet: string;
  playerLevelId: string;
  ratingsSingles: number;
  ratingsDoubles: number;
  state: string;
  stateAbbreviation: string;
  playerUuid: string;
  showCityToPublic: boolean;
  showStateToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  showMiddleNameToPublic: boolean;
  countryAbbreviation2Digit: string;
  stateTitle: string;
  singlesPercentage: number;
  doublesPercentage: number;
  mixedPercentage: number;
  worldRank: number;
  worldRankingPoints: number;
  worldRankDelta: number;
  singlesRank: number;
  singlesRankingPoints: number;
  singlesRankDelta: number;
  singlesPointsCounting: number;
  doublesRank: number;
  doublesRankingPoints: number;
  doublesRankDelta: number;
  doublesPointsCounting: number;
  mixedRank: number;
  mixedRankingPoints: number;
  mixedRankDelta: number;
  mixedPointsCounting: number;
}

export interface FormattedAPRRankingProfileData {
  firstName: string;
  lastName: string;
  playerImage: string;
  abbreviation: string;
  countryFlag: string;
  playerLevelId: string;
  singlesPercentage: number;
  doublesPercentage: number;
  mixedPercentage: number;
  worldRank: string;
  worldRankingPoints: number;
  worldRankDelta: number;
  singlesRank: string;
  singlesRankingPoints: number;
  singlesRankDelta: number;
  singlesPointsCounting: number;
  doublesRank: string;
  doublesRankingPoints: number;
  doublesRankDelta: number;
  doublesPointsCounting: number;
  mixedRank: string;
  mixedRankingPoints: number;
  mixedRankDelta: number;
  mixedPointsCounting: number;
}

interface RankingsCoordinate {
  month: string;
}

interface RankingsValue {
  rank: string;
  rankingPoints: string;
}

export interface RankingDataPoint {
  rankingsXCoordinate: RankingsCoordinate;
  rankingsYCoordinate: RankingsValue;
}

export interface PlayerProfileGraphData {
  firstName: string;
  lastName: string;
  singlesRankings: RankingDataPoint[];
  doublesRankings: RankingDataPoint[];
  mixedRankings: RankingDataPoint[];
}

export interface PlayerInfo {
  firstName: string;
  lastName: string;
  imageUrl: string | null;
}

export interface Team {
  playerOneUuid: string | null;
  playerTwoUuid: string | null;
  playerOne: PlayerInfo | null;
  playerTwo: PlayerInfo | null;
}

export interface Match {
  eventUuid: string;
  matchUuid: string;
  matchNumber: number;
  matchCompleted: string;
  winner: number;
  scoreFormatGameBestOutOf: number;
  matchVideoUrl: string | null;
  teamOneGameOneScore: number;
  teamOneGameTwoScore: number;
  teamOneGameThreeScore: number;
  teamOneGameFourScore: number;
  teamOneGameFiveScore: number;
  teamTwoGameOneScore: number;
  teamTwoGameTwoScore: number;
  teamTwoGameThreeScore: number;
  teamTwoGameFourScore: number;
  teamTwoGameFiveScore: number;
  teamOne: Team;
  teamTwo: Team;
  roundNumber?: number;
  roundText?: string;
}

export interface EventDetails {
  eventUuid: string;
  eventName: string;
  aggregatePoints: string;
  additionalAggregatePoints: string;
  goldTeam: Team;
  silverTeam: Team;
  bronzeTeam: Team;
  fourthTeam: Team;
  matches: Match[];
  bracketValue: string;
  playerPlacement?: string;
  maxPoints?: string;
  eventRank: number;
  totalNumOfEvents: number;
}

export interface Tournament {
  tournamentName: string;
  tournamentUuid: string;
  startDate: string;
  endDate: string;
  aggregatePoints: string;
  pointsPerEvent: EventDetails[];
  tournamentValuePoints: string;
  tournamentRank: number;
  totalNumOfTournaments: number;
}

export interface TournamentsResultsData {
  firstName: string;
  lastName: string;
  age: string;
  gender: string;
  city: string;
  aggregatePoints: string;
  availableRaceFilterYears: string[];
  pointsPerTournament: Tournament[];
  topTournaments: Tournament[];
  aggregatePointsTop?: string;
}

export interface TournamentStrengthCamel {
  tournamentUuid: string;
  tournamentStrength: number;
  numberOfEvents: number;
  numberOfPlayers: number;
  playerRating: number;
  partnerRating: number;
  averageNumberOfEvents: number;
  averageNumberOfPlayers: number;
  averagePlayerRating: number;
  averageTeamRating: number;
  averageTournamentStrength: number;
  tournamentRank: number;
  totalNumOfTournaments: number;
  tournamentRankPercentage: number;
}

export interface BracketStrengthData {
  eventUuid: string;
  bracketType: string;
  currentUsersPlacement: number;
  numberOfMatches: number;
  numberOfPlayers: number;
  averagePlayerRating: number;
  averageTeamRating: number;
  averageNumberOfMatches: number;
  averageNumberOfPlayers: number;
  averageBracketPlayerRating: number;
  averageBracketTeamRating: number;
  bracketStrength: number;
  averageBracketStrength: number;
  playerRating: number;
  partnerRating: number;
  eventRankPercentage: number;
  eventRank: number;
  totalNumOfEvents: number;
}

export interface EligibilityAndExclusionsData {
  minNumberOfTournamentsPlayed: number;
  countBestOfTournaments: number;
  excludedProLevelTitles: string[];
  numOfAmateurPlayers: number;
  numOfTournaments: number;
}

export type MenuSection = 'search' | 'country' | 'state' | 'rankBy' | 'age' | null;

export interface TournamentRankingData {
  tournamentUuid: string;
  tournamentRank: string;
  title: string;
  tournamentTotalPoints: string;
  locationCountry: string;
  locationState: string;
  numberOfEvents: string;
  numberOfPlayers: string;
  startDate: string;
  endDate: string;
  tournamentRankPercentage: number;
  strengthMultiplier: number;
  year: string;
  rank52Weeks: string;
  activeLinkedTournamentUuid: string;
  activeLinkedTournamentTitle: string;
  activeLinkedTournamentCheckoutVersion: number;
}

export interface TournamentRankingStats {
  tournamentsIn52WeeksCount: string;
  topPoints: string;
  averagePoints: number;
  ytdTournamentsCount: string;
}

export interface TournamentRankingFormattedStats {
  tournamentsIn52WeeksCount: string;
  topStrength: string;
  averageStrength: string;
  ytdTournamentsCount: string;
}

export interface TournamentRankingDataRes {
  response: TournamentRankingData[];
  stats: TournamentRankingStats;
}

export interface TournamentInfoData {
  tournamentUuid: string;
  tournamentTitle: string;
  tournamentRank: string;
  tournamentPercentage: number;
  startDate: string;
  endDate: string;
  tournamentStrength: string;
  numberOfEvents: string;
  numberOfPlayers: string;
  tournamentCountry: string;
  tournamentState: string;
  logo: string;
  events: TournamentEventData[];
  totalTournaments: string;
  countryAbbreviationTwoDigit: string;
  strengthMultiplier: number;
  tournamentRankPerState: string;
  tournamentRankPerCountry: string;
  totalTournamentsPerState: string;
  totalTournamentsPerCountry: string;
  isRanked52Weeks: boolean;
  rank52Weeks: string;
  rankCountry52Weeks: string;
  rankState52Weeks: string;
  totalTournaments52Weeks: string;
  totalTournamentsCountry52Weeks: string;
  totalTournamentsState52Weeks: string;
  rankPercentage52Weeks: number;
  strengthMultiplier52Weeks: number;
  year: string;
  activeLinkedTournamentUuid: string;
  activeLinkedTournamentTitle: string;
  activeLinkedTournamentCheckoutVersion: number;
}

export interface TournamentEventData {
  eventUuid: string;
  eventTitle: string;
  eventDivisionType: NumDivisionTypes;
  startDate: string;
  endDate: string;
  eventRank: string;
  eventRankPercentage: number;
  eventStrength: string;
  numberOfPlayers: string;
  bracketFormat: string;
  totalEventsByFormat: string;
  bracketLevel: string;
}

export interface TournamentEventsData {
  data: RenderTournamentEventData[];
  totalRecords: number;
}

export interface RenderTournamentEventData {
  eventUuid: string;
  eventTitle: string;
  startDate: string;
  eventRank: string;
  eventRankPercentage: number;
  eventStrength: string;
  numberOfPlayers: string;
  totalEventsByFormat: string;
  bracketFormat: string;
  eventDivisionType: NumDivisionTypes;
  bracketLevel: string;
}

export type NumDivisionTypes = '1' | '2' | '3' | '4' | '5';
export type DivisionTypes =
  | 'Mens Doubles'
  | 'Womens Doubles'
  | 'Mixed Doubles'
  | 'Mens Singles'
  | 'Womens Singles';

export interface RenderTournamentGroupedEventData {
  header: string;
  bracketLevel: string;
  divisionType: NumDivisionTypes;
  event: RenderTournamentEventData;
}

export interface HeaderInfoItemType {
  label: string;
  info: string;
  flag?: string;
  borderRightDesktop?: boolean;
  borderRightMobile?: boolean;
}

export interface TournamentInfoHeaderData {
  uuid: string;
  title: string;
  rank: string;
  percentage: number;
  startDate: string;
  endDate: string;
  strength: string;
  numberOfEvents: string;
  numberOfPlayers: string;
  country: string;
  state: string;
  logo: string;
  totalTournaments: string;
  countryFlag: string;
  strengthMultiplier: number;
  tournamentRankPerState: string;
  tournamentRankPerCountry: string;
  totalTournamentsPerState: string;
  totalTournamentsPerCountry: string;
  isRanked52Weeks: boolean;
  rank52Weeks: string;
  rankCountry52Weeks: string;
  rankState52Weeks: string;
  totalTournaments52Weeks: string;
  totalTournamentsCountry52Weeks: string;
  totalTournamentsState52Weeks: string;
  rankPercentage52Weeks: number;
  strengthMultiplier52Weeks: number;
  year: string;
  activeLinkedTournamentUuid: string;
  activeLinkedTournamentCheckoutVersion: number;
}

export interface GetTournamentInfoHeaderRes {
  tournament: TournamentInfoHeaderData | undefined;
}

export type RankTypeValue = 'world' | 'country' | 'state';

export interface RankType {
  id: number;
  label: string;
  value: RankTypeValue;
}

export interface CsvExportPlayerRankingsParams {
  rankings?: string;
  search?: string;
  country?: string;
  state?: string;
  rankBy?: string;
  age?: string;
  statsType?: string;
  genderType?: string;
}

export interface CsvExportTournamentRankingsParams {
  search?: string;
  country?: string;
  state?: string;
  year?: string;
}

export interface TableHeaderInterface {
  width: string;
  label: string;
  sticky?: boolean;
  tooltip?: boolean;
}

export enum RankByLabels {
  WEEKS_52 = '52 Weeks',
  WORLD = 'World Ranking',
}

export interface CountdownError {
  statusCode?: number;
  error?: string;
}

export interface CountdownInterface {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}
